import { useEffect, useState } from 'react'
import { dateText, translate, type Lang } from './i18n'
import type { Handover, HandoverSnapshot } from './api'
import { useShiftCodes } from './shifts'

/**
 * La entrega de turno (§10.1).
 *
 * Es el único momento de la operación que el sistema no acompañaba: dos supervisores se relevaban
 * mirando la misma pantalla y acordando de palabra. Lo que faltaba no era el dato —está todo en el
 * Centro de Mando— sino la constancia de quién dijo qué a quién y a qué hora.
 *
 * Por eso la pantalla tiene dos caras. Antes de entregar muestra **lo que se entregaría**, para
 * que quien firma vea lo que está a punto de decir. Después muestra **lo que se entregó**, que no
 * es lo mismo: es la foto del instante del relevo, y no cambia aunque la situación sí.
 */
export function HandoverPanel({
  lang, shiftDate, shiftCode, onShift, preview, handover, busy, canSign, actor,
  onDeliver, onReceive,
}: {
  lang: Lang
  shiftDate: string
  shiftCode: string
  onShift: (date: string, code: string) => void
  preview: HandoverSnapshot | null
  handover: Handover | null
  busy: boolean
  canSign: boolean
  /** Quién mira: quien entregó no puede recibir, y el botón no debe ofrecerlo. */
  actor: string
  onDeliver: (notes: string) => void
  onReceive: (handoverId: string, note: string) => void
}) {
  const shifts = useShiftCodes()
  const [notes, setNotes] = useState('')
  const [receipt, setReceipt] = useState('')

  // Al cambiar de turno, lo escrito para el anterior deja de tener sentido.
  useEffect(() => { setNotes(''); setReceipt('') }, [shiftDate, shiftCode])

  const foto = handover?.snapshot ?? preview
  const abierto = foto !== null && (
    foto.alerts.length > 0 || foto.incidents.length > 0 ||
    foto.units.length > 0 || foto.assignments.length > 0)

  const sinReportar = foto?.units.filter((u) => !u.reporting).length ?? 0
  const criticas = foto?.alerts.filter((a) => a.severity === 'Critical').length ?? 0

  const resumen = (f: HandoverSnapshot) => (
    <div className="card">
      <div className="meta">
        {translate(lang, 'handover.counts', [
          f.units.length, f.assignments.length, f.alerts.length, f.incidents.length,
        ])}
      </div>
      {/* Las dos cosas que no pueden esperar al café van aparte del conteo. */}
      {criticas > 0 && (
        <div className="row-line">
          <span className="chip warning">{translate(lang, 'handover.critical', [criticas])}</span>
        </div>
      )}
      {sinReportar > 0 && (
        <div className="row-line">
          <span className="chip warning">{translate(lang, 'handover.silent', [sinReportar])}</span>
        </div>
      )}
      {f.units.length > 0 && (
        <div className="meta">
          {translate(lang, 'handover.stillOut')}: {f.units.map((u) => u.callSign).join(', ')}
        </div>
      )}
      {f.incidents.map((i) => (
        <div key={i.incidentId} className="meta">
          {i.callSign} · {i.description} ({translate(lang, `status.${i.status}`)})
        </div>
      ))}
    </div>
  )

  return (
    <section className="panel">
      <h2>{translate(lang, 'handover.title')}</h2>
      <p className="notice">{translate(lang, 'handover.help')}</p>

      <div className="card">
        <div className="row-line" style={{ gap: 6 }}>
          <input
            type="date" value={shiftDate} disabled={busy}
            onChange={(e) => onShift(e.target.value, shiftCode)}
            aria-label={translate(lang, 'replan.shiftDate')}
          />
          <select
            value={shiftCode} disabled={busy}
            onChange={(e) => onShift(shiftDate, e.target.value)}
            aria-label={translate(lang, 'replan.shiftCode')}
          >
            {shifts.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      {handover === null ? (
        <>
          <h3>{translate(lang, 'handover.wouldHandOver')}</h3>
          {foto !== null && resumen(foto)}
          {!abierto && <p className="notice">{translate(lang, 'handover.clean')}</p>}

          {canSign && (
            <div className="card">
              <textarea
                rows={3} value={notes} disabled={busy} style={{ width: '100%' }}
                placeholder={translate(lang, abierto ? 'handover.notesRequired' : 'handover.notes')}
                onChange={(e) => setNotes(e.target.value)}
                aria-label={translate(lang, 'handover.notes')}
              />
              <button
                className="primary"
                disabled={busy || (abierto && notes.trim() === '')}
                onClick={() => onDeliver(notes.trim())}
              >
                {translate(lang, 'handover.deliver')}
              </button>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="card">
            <div className="row-line">
              <strong className="grow">
                {translate(lang, 'handover.deliveredBy', [handover.deliveredBy])}
              </strong>
              <span className={handover.status === 'Received' ? 'chip ok' : 'chip'}>
                {translate(lang, `handoverStatus.${handover.status}`)}
              </span>
            </div>
            <div className="meta">{dateText(lang, handover.shiftDate)} · {handover.shiftCode}</div>
            {handover.notes !== '' && <p className="notice">{handover.notes}</p>}
          </div>

          <h3>{translate(lang, 'handover.asDelivered')}</h3>
          <p className="notice">{translate(lang, 'handover.frozen')}</p>
          {resumen(handover.snapshot)}

          {handover.status === 'Received' ? (
            <div className="card">
              <div className="meta">
                {translate(lang, 'handover.receivedBy', [handover.receivedBy])}
              </div>
              {handover.receiptNote !== '' && <p className="notice">{handover.receiptNote}</p>}
            </div>
          ) : canSign && (
            <div className="card">
              {/* Quien entrega no puede recibir: ofrecerle el botón sería invitarlo a un 409. */}
              {handover.deliveredBy.toLowerCase() === actor.toLowerCase() ? (
                <p className="notice">{translate(lang, 'handover.waitingForOther')}</p>
              ) : (
                <>
                  <input
                    value={receipt} disabled={busy} style={{ width: '100%', marginBottom: 8 }}
                    placeholder={translate(lang, 'handover.receiptNote')}
                    onChange={(e) => setReceipt(e.target.value)}
                    aria-label={translate(lang, 'handover.receiptNote')}
                  />
                  <button
                    className="primary" disabled={busy}
                    onClick={() => onReceive(handover.handoverId, receipt.trim())}
                  >
                    {translate(lang, 'handover.receive')}
                  </button>
                </>
              )}
            </div>
          )}
        </>
      )}
    </section>
  )
}
